import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ChevronDown, Check } from 'lucide-react';
import { LandingNav } from '../components/landing-nav';
import { LandingFooter } from '../components/landing-footer';
import { AddonsPricing } from '../components/addons-pricing';

export function PricingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Scroll to top instantly on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <LandingNav />

      {/* ------------------------------------------------------------------ */}
      {/* Hero section                                                         */}
      {/* ------------------------------------------------------------------ */}
      <section className="pt-28 pb-8 md:pt-36 md:pb-12 text-center px-4 relative overflow-hidden">
        {/* Subtle background glow */}
        <div
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-[0.07] bg-primary blur-[100px] pointer-events-none"
          aria-hidden="true"
        />

        <div className="relative z-10 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-5 bg-accent/10 text-accent">
            <Sparkles size={12} className="fill-current" />
            <span>{t('pricingPage.badge', 'Preços simples e transparentes')}</span>
          </div>

          <h1 className="text-2xl md:text-4xl lg:text-5xl font-serif font-bold leading-[1.1] tracking-tight text-primary mb-4">
            {t('pricingPage.heroTitleLine1', 'Monte seu plano')}
            <br />
            <span className="italic text-accent">
              {t('pricingPage.heroTitleLine2', 'add-on por add-on')}
            </span>
          </h1>

          <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
            {t(
              'pricingPage.heroSubtitle',
              'Ao criar a conta você ganha 7 dias de tudo, grátis e sem cartão — inclusive colaboração em tempo real. Depois, siga no Grátis ou ative só os add-ons que precisar. Empilháveis, cancele quando quiser.'
            )}
          </p>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Add-ons pricing                                                      */}
      {/* ------------------------------------------------------------------ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">
        <AddonsPricing variant="public" />
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* What's always free — reassurance block                              */}
      {/* ------------------------------------------------------------------ */}
      <section className="bg-card border-y border-border py-10 md:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-xl md:text-2xl lg:text-3xl font-bold text-primary mb-3">
            {t('pricingPage.freeIncludedTitle', 'O que está sempre incluso no plano Grátis')}
          </h2>
          <p className="text-muted-foreground text-sm md:text-base mb-8">
            {t(
              'pricingPage.freeIncludedSubtitle',
              'Você sempre pode escrever — os add-ons ampliam as superpotências.'
            )}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-left">
            {[
              t('pricingPage.featureEditor', 'Editor completo sem limitação de palavras'),
              t('pricingPage.featureDarkMode', 'Dark mode e temas moonlight'),
              t('pricingPage.featureExport', 'Export PDF e DOCX'),
              t('pricingPage.featureAiActions', '30 ações de IA por mês (texto)'),
              t('pricingPage.featureCoverGeneration', '1 geração de capa por mês'),
              t('pricingPage.featureUnlimitedProjects', '1 projeto no plano Grátis'),
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2 rounded-xl border border-border bg-muted/20 px-3 py-2.5 text-xs text-muted-foreground"
              >
                <Check className="h-3.5 w-3.5 text-accent mt-0.5 shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* FAQ                                                                  */}
      {/* ------------------------------------------------------------------ */}
      <section className="max-w-3xl mx-auto px-4 py-10 md:py-20">
        <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-primary text-center mb-3">
          {t('pricingPage.faqTitle', 'Perguntas frequentes')}
        </h2>
        <p className="text-muted-foreground text-center mb-8 md:mb-12 text-sm md:text-base">
          {t('pricingPage.faqSubtitle', 'Tem outra dúvida? Entre em contato com nosso suporte.')}
        </p>

        <div className="space-y-2.5 md:space-y-3">
          {[
            {
              q: t('pricingPage.faqCombineQuestion', 'Posso combinar vários add-ons?'),
              a: t(
                'pricingPage.faqCombineAnswer',
                'Sim! Os add-ons são totalmente empilháveis. Você pode ter IA Studio + Craft + Outlining ao mesmo tempo, ou simplesmente pegar o Bundle "Tudo" para economizar ~20%.'
              ),
            },
            {
              q: t(
                'pricingPage.faqCancelQuestion',
                'O que acontece com meus dados se eu cancelar um add-on?'
              ),
              a: t(
                'pricingPage.faqCancelAnswer',
                'Seus projetos ficam salvos. Você perde acesso às funções premium do add-on cancelado, mas pode reativar a qualquer momento.'
              ),
            },
            {
              q: t('pricingPage.faqRefundQuestion', 'Vocês oferecem reembolso?'),
              a: t(
                'pricingPage.faqRefundAnswer',
                'Sim, garantia de 7 dias. Se não gostar, devolvemos seu dinheiro sem perguntas.'
              ),
            },
            {
              q: t('pricingPage.faqAiStudioQuestion', 'Como funciona o IA Studio?'),
              a: t(
                'pricingPage.faqAiStudioAnswer',
                'Com IA Studio você tem acesso generoso (fair-use) a reescrita, mudança de tom, expansão de texto e RAG sobre seu próprio livro. Inclui 10 gerações de capa/mês.'
              ),
            },
            {
              q: t('pricingPage.faqPaymentQuestion', 'Quais formas de pagamento vocês aceitam?'),
              a: t(
                'pricingPage.faqPaymentAnswer',
                'PIX, cartão de crédito (até 12x) e boleto bancário, processados pela Asaas.'
              ),
            },
            {
              q: t('pricingPage.faqAnnualQuestion', 'O plano anual realmente é mais barato?'),
              a: t(
                'pricingPage.faqAnnualAnswer',
                'Sim — o desconto anual equivale a ~17% de economia, ou aproximadamente 2 meses grátis por add-on.'
              ),
            },
          ].map((faq, i) => (
            <details
              key={i}
              className="group bg-card border border-border rounded-2xl overflow-hidden"
            >
              <summary className="px-5 py-4 md:px-6 cursor-pointer font-medium text-sm md:text-base text-foreground hover:bg-muted/40 transition-colors list-none flex items-center justify-between gap-3 touch-manipulation">
                <span>{faq.q}</span>
                <ChevronDown
                  size={18}
                  className="flex-shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180"
                />
              </summary>
              <div className="px-5 pb-4 md:px-6 md:pb-5 text-xs md:text-sm text-muted-foreground leading-relaxed border-t border-border pt-3 md:pt-4">
                {faq.a}
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Footer CTA                                                           */}
      {/* ------------------------------------------------------------------ */}
      <section className="bg-primary py-14 md:py-20 px-4 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-5 bg-gradient-to-br from-primary-foreground/10 to-transparent pointer-events-none"
          aria-hidden="true"
        />
        <div className="relative z-10 max-w-2xl mx-auto">
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-primary-foreground mb-4">
            {t('pricingPage.ctaTitle', 'Pronto para começar?')}
          </h2>
          <p className="text-primary-foreground/70 mb-7 md:mb-8 text-base md:text-lg">
            {t(
              'pricingPage.ctaSubtitle',
              'Junte-se a milhares de escritores que já estão usando o reformu.lat'
            )}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <button
              onClick={() => navigate('/app?auth=signup')}
              className="w-full sm:w-auto px-7 py-3.5 md:px-8 md:py-4 rounded-full font-medium text-sm md:text-base transition-all hover:scale-105 hover:shadow-lg bg-accent text-accent-foreground"
            >
              {t('pricingPage.ctaSignup', 'Criar Conta Grátis')}
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto px-7 py-3.5 md:px-8 md:py-4 rounded-full font-medium text-sm md:text-base border-2 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 transition-colors"
            >
              {t('pricingPage.ctaLearnMore', 'Ver mais sobre a plataforma')}
            </button>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
